import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client'
import { eqIgnoreCase, extendedCast } from '@arcxmoney/arcx-core'
import { poll } from '@arcxmoney/arcx-react'
import { asBigNumber, asEthAddress } from '@restless/ethereum'
import { asArray, asObject } from '@restless/sanitizers'
import { BigNumber, constants } from 'ethers'
import { useEffect, useState } from 'react'

export interface AccountBalance {
  asset: string
  account: string
  balance: BigNumber
}

export class BalanceService {
  private balances: AccountBalance[]

  constructor(private client: ApolloClient<NormalizedCacheObject>) {}

  async fetchBalances(accountAddress: string) {
    const balancesData = await this.client.query({
      query: GET_ACCOUNT_BALANCES_FOR_ACCOUNT,
      variables: { account: accountAddress },
      fetchPolicy: 'network-only',
    })
    return extendedCast(
      balancesData.data.accountBalances,
      asArray(asAccountBalance),
      `Cannot cast AccountBalance from GraphQL: ${JSON.stringify(
        balancesData.data.accountBalances,
      )}`,
    )
  }

  subscribe(account: string, callback: (balances: AccountBalance[]) => void) {
    return poll(async () => {
      const balances = (await this.fetchBalances(account)).sort()
      if (JSON.stringify(balances) !== JSON.stringify(this.balances)) {
        this.balances = balances
        callback(balances)
      }
    }, 5000)
  }

  getSyntheticBalance(asset: string) {
    const accountBalance = this.balances.find((balance) => balance.asset === asset)
    return accountBalance ? accountBalance.balance : constants.Zero
  }
}

const asAccountBalance = asObject<AccountBalance>({
  account: asEthAddress,
  asset: asEthAddress,
  balance: asBigNumber,
})

export const GET_ACCOUNT_BALANCES_FOR_ACCOUNT = gql`
  query AccountBalance($account: Bytes!) {
    accountBalances(where: { account: $account }) {
      id
      account
      asset
      balance
    }
  }
`

export const useSyntheticBalances = (
  apolloClient: ApolloClient<NormalizedCacheObject>,
  accountAddress?: string,
) => {
  const [graphBalances, setGraphBalances] = useState<AccountBalance[]>([])
  const getSyntheticBalance = (assetAddress: string) =>
    graphBalances.find(({ asset }) => eqIgnoreCase(asset, assetAddress))?.balance || constants.Zero

  useEffect(() => {
    if (!accountAddress) {
      return
    }
    return new BalanceService(apolloClient).subscribe(accountAddress, setGraphBalances)
  }, [accountAddress, apolloClient])

  return {
    balances: graphBalances,
    getSyntheticBalance,
  }
}
